import React, { useState, useCallback } from 'react';
import { Navigation } from '../components/Navigation';
import { Newsletter } from '../components/Newsletter';
import { motion, AnimatePresence } from 'framer-motion';
import { FileUp, Upload, FileText, Image, AlertCircle, CheckCircle2, XCircle, Search, Filter, Download, Trash2, RefreshCw } from 'lucide-react';
import { processCSV, processPDF, extractTextFromImage } from '../utils/fileProcessing';
import { FinancialChart } from '../components/Chart';
import toast from 'react-hot-toast';

interface Transaction {
  id: string;
  date: string;
  description: string;
  amount: number;
  category: string;
  type: 'income' | 'expense';
}

interface CategoryTotal {
  category: string;
  amount: number;
  percentage: number;
}

const DocumentAnalysis = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [sortConfig, setSortConfig] = useState({ field: 'date', direction: 'desc' });

  const handleFileDrop = useCallback(async (files: FileList) => {
    const file = files[0];
    if (!file) return;

    setIsProcessing(true);
    try {
      let data;
      if (file.type === 'text/csv') {
        data = await processCSV(file);
        toast.success('CSV file processed successfully');
      } else if (file.type === 'application/pdf') {
        data = await processPDF(file);
        toast.success('PDF file processed successfully');
      } else if (file.type.startsWith('image/')) {
        data = await extractTextFromImage(file);
        toast.success('Image processed successfully');
      } else {
        throw new Error('Unsupported file type');
      }

      // Process and categorize transactions
      const processedTransactions = processTransactions(data);
      setTransactions(prev => [...prev, ...processedTransactions]);
    } catch (error) {
      console.error('Error processing file:', error);
      toast.error('Failed to process file');
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const processTransactions = (data: any): Transaction[] => {
    // Simple transaction processing logic that runs in the browser
    try {
      // Assuming data is an array of records with date, description, and amount
      return data.map((record: any) => ({
        id: crypto.randomUUID(), // Generate a unique ID
        date: record.date || new Date().toISOString(),
        description: record.description || 'Unknown Transaction',
        amount: parseFloat(record.amount) || 0,
        category: categorizeTransaction(record.description || ''),
        type: parseFloat(record.amount) >= 0 ? 'income' : 'expense'
      }));
    } catch (error) {
      console.error('Error processing transactions:', error);
      return [];
    }
  };

  const categorizeTransaction = (description: string): string => {
    // Simple categorization based on keywords
    const keywords = {
      'grocery': ['food', 'grocery', 'market', 'supermarket'],
      'transport': ['uber', 'lyft', 'taxi', 'bus', 'train'],
      'utilities': ['electric', 'water', 'gas', 'internet'],
      'entertainment': ['netflix', 'spotify', 'movie', 'theatre'],
      'shopping': ['amazon', 'walmart', 'target', 'store']
    };

    description = description.toLowerCase();
    for (const [category, words] of Object.entries(keywords)) {
      if (words.some(word => description.includes(word))) {
        return category;
      }
    }
    return 'other';
  };

  const calculateCategoryTotals = (): CategoryTotal[] => {
    const totals: Record<string, number> = {};
    let total = 0;

    transactions.forEach(transaction => {
      if (!totals[transaction.category]) {
        totals[transaction.category] = 0;
      }
      totals[transaction.category] += transaction.amount;
      total += transaction.amount;
    });

    return Object.entries(totals).map(([category, amount]) => ({
      category,
      amount,
      percentage: (amount / total) * 100
    }));
  };

  const chartData = {
    labels: calculateCategoryTotals().map(cat => cat.category),
    datasets: [{
      label: 'Spending by Category',
      data: calculateCategoryTotals().map(cat => cat.amount),
      backgroundColor: [
        'rgba(16, 185, 129, 0.8)',
        'rgba(52, 211, 153, 0.8)',
        'rgba(5, 150, 105, 0.8)',
        'rgba(4, 120, 87, 0.8)',
        'rgba(6, 95, 70, 0.8)'
      ]
    }]
  };

  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = transaction.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || transaction.category === selectedCategory;
    const matchesDate = (!dateRange.start || new Date(transaction.date) >= new Date(dateRange.start)) &&
                       (!dateRange.end || new Date(transaction.date) <= new Date(dateRange.end));
    return matchesSearch && matchesCategory && matchesDate;
  });

  const exportData = () => {
    const csv = [
      ['Date', 'Description', 'Amount', 'Category', 'Type'],
      ...filteredTransactions.map(t => [
        t.date,
        t.description,
        t.amount.toString(),
        t.category,
        t.type
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-[var(--background)] p-6">
      <div className="max-w-7xl mx-auto">
        <Navigation />

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="p-3 rounded-xl bg-emerald-500/10">
              <FileUp className="w-10 h-10 text-emerald-500" />
            </div>
            <h1 className="text-4xl font-bold">Document Analysis</h1>
          </div>
          <p className="text-slate-400 text-lg">
            Upload and analyze your financial documents
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            {/* Upload Section */}
            <div
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFileDrop(e.dataTransfer.files);
              }}
              className="glass-card p-8 border-2 border-dashed border-emerald-500/20 text-center hover:border-emerald-500/40 transition-colors"
            >
              <Upload className="w-12 h-12 text-emerald-500 mx-auto mb-4" />
              <p className="text-slate-300 mb-2">
                Drag and drop your financial documents here
              </p>
              <p className="text-sm text-slate-400 mb-4">
                Supports CSV, PDF, and image files
              </p>

              <input
                type="file"
                onChange={(e) => e.target.files && handleFileDrop(e.target.files)}
                className="hidden"
                accept=".csv,.pdf,.jpg,.jpeg,.png"
                id="file-upload"
              />

              <motion.label
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                htmlFor="file-upload"
                className="inline-block px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors cursor-pointer"
              >
                Select File
              </motion.label>

              {isProcessing && (
                <div className="mt-4 flex items-center justify-center gap-2 text-emerald-500">
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Processing document...
                </div>
              )}
            </div>

            {/* File Type Info */}
            <div className="grid grid-cols-3 gap-4">
              <div className="glass-card p-4 border border-emerald-500/20">
                <FileText className="w-8 h-8 text-emerald-500 mb-2" />
                <h3 className="font-medium mb-1">CSV Files</h3>
                <p className="text-sm text-slate-400">Bank statements and transaction logs</p>
              </div>
              <div className="glass-card p-4 border border-emerald-500/20">
                <FileText className="w-8 h-8 text-emerald-500 mb-2" />
                <h3 className="font-medium mb-1">PDF Files</h3>
                <p className="text-sm text-slate-400">Financial statements and reports</p>
              </div>
              <div className="glass-card p-4 border border-emerald-500/20">
                <Image className="w-8 h-8 text-emerald-500 mb-2" />
                <h3 className="font-medium mb-1">Images</h3>
                <p className="text-sm text-slate-400">Receipts and invoices</p>
              </div>
            </div>

            {/* Analysis Chart */}
            {transactions.length > 0 && (
              <div className="glass-card p-6">
                <h3 className="text-xl font-semibold mb-6">Spending Analysis</h3>
                <FinancialChart
                  type="pie"
                  data={chartData}
                  height={300}
                />
              </div>
            )}
          </div>

          <div className="space-y-6">
            {/* Search and Filter */}
            <div className="glass-card p-6">
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="relative">
                  <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search transactions..."
                    className="glass-input pl-10 pr-4 py-2 w-full rounded-lg"
                  />
                </div>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="glass-input px-4 py-2 rounded-lg"
                >
                  <option value="">All Categories</option>
                  {Array.from(new Set(transactions.map(t => t.category))).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="glass-input px-4 py-2 w-full rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-2">End Date</label>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="glass-input px-4 py-2 w-full rounded-lg"
                  />
                </div>
              </div>
            </div>

            {/* Transaction List */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold">Transactions</h3>
                {transactions.length > 0 && (
                  <button
                    onClick={exportData}
                    className="px-4 py-2 bg-emerald-500/10 text-emerald-500 rounded-lg hover:bg-emerald-500/20 transition-colors flex items-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Export
                  </button>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">Description</th>
                      <th className="text-right p-3">Amount</th>
                      <th className="text-left p-3">Category</th>
                      <th className="text-right p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map(transaction => (
                      <tr key={transaction.id} className="border-b border-slate-800">
                        <td className="p-3">{new Date(transaction.date).toLocaleDateString()}</td>
                        <td className="p-3">{transaction.description}</td>
                        <td className={`text-right p-3 ${
                          transaction.type === 'income' ? 'text-emerald-500' : 'text-red-500'
                        }`}>
                          ${Math.abs(transaction.amount).toLocaleString()}
                        </td>
                        <td className="p-3">{transaction.category}</td>
                        <td className="p-3 text-right">
                          <button
                            onClick={() => {
                              setTransactions(prev => prev.filter(t => t.id !== transaction.id));
                              toast.success('Transaction removed');
                            }}
                            className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredTransactions.length === 0 && (
                  <div className="text-center py-8 text-slate-400">
                    <FileUp className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No transactions found. Upload a document to get started!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8">
          <Newsletter 
            title="Stay Updated"
            subtitle="Get the latest tips on managing your finances and organizing financial documents."
          />
        </div>
      </div>
    </div>
  );
};

export default DocumentAnalysis;